// modules/FragmentSystem.js
// 碎片合成系统模块
import DebugHelper from './DebugHelper.js';
import InventoryHelper from './InventoryHelper.js';
import BlueprintSystem from './BlueprintSystem.js';
import MaterialSystem from './MaterialSystem.js';
import { deepClone } from './utils/PlayerDataSync.js';

export default class FragmentSystem {
    /**
     * 检查是否可以合成装备（需要图纸+材料+碎片）
     * @param {Object} player - 玩家对象
     * @param {string} equipmentId - 装备ID
     * @param {number} tier - 装备等级
     * @param {Object} equipmentTemplate - 装备模板（包含合成配方）
     * @returns {Object} 检查结果 {canCraft: boolean, fragments: number, required: number, message: string, missing: Array}
     */
    static canCraftEquipment(player, equipmentId, tier = 1, equipmentTemplate = null) {
        if (!player || !player.inventory) {
            return {
                canCraft: false,
                fragments: 0,
                required: 0,
                message: '玩家数据无效',
                missing: []
            };
        }
        
        const missing = [];
        
        // 1. 检查是否有图纸
        if (!BlueprintSystem.hasBlueprint(player, equipmentId)) {
            missing.push('图纸');
            return {
                canCraft: false,
                fragments: 0,
                required: 0,
                message: `缺少图纸：需要${equipmentTemplate?.name || equipmentId}图纸`,
                missing: missing
            };
        }
        
        // 2. 检查碎片
        // 从craftMaterials中读取碎片数量（必须配置）
        let required = 0;
        if (equipmentTemplate && equipmentTemplate.craftMaterials && equipmentTemplate.craftMaterials.fragments !== undefined) {
            required = equipmentTemplate.craftMaterials.fragments;
        } else {
            // 如果没有配置碎片数量，返回错误
            return {
                canCraft: false,
                fragments: 0,
                required: 0,
                message: `装备 ${equipmentTemplate?.name || equipmentId} 未配置合成配方（缺少fragments）`,
                missing: ['合成配方配置错误']
            };
        }
        
        const fragmentId = `${equipmentId}_fragment`;
        
        let fragments = 0;
        player.inventory.forEach(item => {
            if (item.id === fragmentId || item.name === `${equipmentTemplate?.name || equipmentId}碎片`) {
                fragments++;
            }
        });
        
        if (fragments < required) {
            missing.push(`碎片（需要${required}个，拥有${fragments}个）`);
        }
        
        // 3. 检查材料（如果有合成配方）
        if (equipmentTemplate && equipmentTemplate.craftMaterials) {
            const materials = MaterialSystem.getPlayerMaterials(player);
            const craftMaterials = equipmentTemplate.craftMaterials;
            
            Object.entries(craftMaterials).forEach(([materialId, requiredCount]) => {
                // 跳过fragments字段，它不是材料
                if (materialId === 'fragments') return;
                
                const playerCount = materials[materialId] || 0;
                if (playerCount < requiredCount) {
                    const materialName = MaterialSystem.getMaterialData(materialId)?.name || materialId;
                    missing.push(`${materialName}（需要${requiredCount}个，拥有${playerCount}个）`);
                }
            });
        }
        
        if (missing.length > 0) {
            return {
                canCraft: false,
                fragments: fragments,
                required: required,
                message: `材料不足：${missing.join('、')}`,
                missing: missing
            };
        }
        
        return {
            canCraft: true,
            fragments: fragments,
            required: required,
            message: `可以合成（拥有 ${fragments}/${required} 个碎片）`,
            missing: []
        };
    }
    
    /**
     * 合成装备（需要图纸+材料+碎片）
     * @param {Object} player - 玩家对象
     * @param {Object} equipmentTemplate - 装备模板（从JSON数据中获取）
     * @param {number} tier - 装备等级
     * @returns {Object} 合成结果 {success: boolean, message: string, equipment?: Object}
     */
    static craftEquipment(player, equipmentTemplate, tier = 1) {
        if (!player || !equipmentTemplate) {
            return {
                success: false,
                message: '参数无效'
            };
        }
        
        const equipmentId = equipmentTemplate.id;
        const check = this.canCraftEquipment(player, equipmentId, tier, equipmentTemplate);
        
        if (!check.canCraft) {
            return {
                success: false,
                message: check.message
            };
        }
        
        // 消耗碎片
        const fragmentId = `${equipmentId}_fragment`;
        let consumed = 0;
        const inventory = player.inventory || [];
        
        for (let i = inventory.length - 1; i >= 0 && consumed < check.required; i--) {
            const item = inventory[i];
            if (item.id === fragmentId || item.name === `${equipmentTemplate.name}碎片`) {
                inventory.splice(i, 1);
                consumed++;
            }
        }
        
        if (consumed < check.required) {
            return {
                success: false,
                message: `碎片消耗失败（需要 ${check.required} 个，只消耗了 ${consumed} 个）`
            };
        }
        
        // 消耗材料（如果有合成配方）
        if (equipmentTemplate.craftMaterials) {
            const craftMaterials = equipmentTemplate.craftMaterials;
            const materials = MaterialSystem.getPlayerMaterials(player);
            
            // 检查材料是否足够（跳过fragments字段）
            for (const [materialId, requiredCount] of Object.entries(craftMaterials)) {
                if (materialId === 'fragments') continue; // 跳过碎片字段
                
                const playerCount = materials[materialId] || 0;
                if (playerCount < requiredCount) {
                    const materialName = MaterialSystem.getMaterialData(materialId)?.name || materialId;
                    return {
                        success: false,
                        message: `${materialName}不足（需要${requiredCount}个，拥有${playerCount}个）`
                    };
                }
            }
            
            // 消耗材料（跳过fragments字段）
            for (const [materialId, requiredCount] of Object.entries(craftMaterials)) {
                if (materialId === 'fragments') continue; // 跳过碎片字段
                
                let consumedCount = 0;
                for (let i = inventory.length - 1; i >= 0 && consumedCount < requiredCount; i--) {
                    const item = inventory[i];
                    if (item.type === 'material' && (item.id === materialId || item.name === MaterialSystem.getMaterialData(materialId)?.name)) {
                        inventory.splice(i, 1);
                        consumedCount++;
                    }
                }
                
                if (consumedCount < requiredCount) {
                    const materialName = MaterialSystem.getMaterialData(materialId)?.name || materialId;
                    return {
                        success: false,
                        message: `${materialName}消耗失败（需要 ${requiredCount} 个，只消耗了 ${consumedCount} 个）`
                    };
                }
            }
        }
        
        // 创建装备（深拷贝模板）
        const equipment = deepClone(equipmentTemplate);
        // 移除合成配方信息（不需要保存到装备中）
        delete equipment.craftMaterials;
        
        // 添加到背包
        if (!player.inventory) {
            player.inventory = [];
        }
        player.inventory.push(equipment);
        
        const materialText = equipmentTemplate.craftMaterials ? 
            `，消耗材料：${Object.entries(equipmentTemplate.craftMaterials).map(([id, count]) => 
                `${MaterialSystem.getMaterialData(id)?.name || id}x${count}`
            ).join('、')}` : '';
        
        DebugHelper.log(`FragmentSystem: 成功合成 ${equipment.name}，消耗 ${consumed} 个碎片${materialText}`);
        
        return {
            success: true,
            message: `成功合成 ${equipment.name}！`,
            equipment: equipment
        };
    }
    
}

